import { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import { getPostBySlug } from "./blogApi";
import { BlogDetail as BlogDetailType } from "./types";
import StreamFieldRenderer from "./blocks/StreamFieldRenderer";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export default function BlogDetail() {
  const colors = tokens(useTheme().palette.mode);
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogDetailType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getPostBySlug(slug)
      .then(setPost)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress sx={{ color: colors.primary[500] }} />
      </Box>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ py: 12, textAlign: "center" }}>
        <Typography variant="h4" sx={{ color: colors.primary[700], mb: 2 }}>
          Post not found
        </Typography>
        <Link component={RouterLink} to="/blog" sx={{ color: colors.primary[400] }}>
          Back to the blog
        </Link>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: colors.primary[800], minHeight: "100vh", py: 6 }}>
      <Container maxWidth="md">
        <Link
          component={RouterLink}
          to="/blog"
          sx={{ color: colors.primary[400], display: "inline-block", mb: 3 }}
        >
          ← Back to the blog
        </Link>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            label={post.content_kind}
            size="small"
            sx={{
              textTransform: "capitalize",
              backgroundColor: colors.primary[600],
              color: colors.primary[400],
              fontWeight: 600,
            }}
          />
          {post.category_api && (
            <Chip
              label={post.category_api.name}
              size="small"
              variant="outlined"
              sx={{ color: colors.primary[300] }}
            />
          )}
        </Stack>

        <Typography variant="h2" sx={{ fontWeight: 800, color: colors.primary[700], mb: 1 }}>
          {post.title}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.primary[300], mb: 3 }}>
          {post.author_name ? `${post.author_name} · ` : ""}
          {formatDate(post.publish_date)}
        </Typography>

        {post.featured_image && (
          <Box
            component="img"
            src={post.featured_image.url}
            alt={post.featured_image.alt || post.title}
            sx={{ width: "100%", height: "auto", borderRadius: 3, mb: 4 }}
          />
        )}

        {post.intro && (
          <Typography
            sx={{ fontSize: "1.2rem", color: colors.primary[700], mb: 4, fontWeight: 500 }}
          >
            {post.intro}
          </Typography>
        )}

        <StreamFieldRenderer body={post.body} />

        {post.tags_list.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 6, flexWrap: "wrap", gap: 1 }}>
            {post.tags_list.map((tag) => (
              <Chip
                key={tag}
                label={`#${tag}`}
                size="small"
                sx={{ backgroundColor: colors.primary[600], color: colors.primary[400] }}
              />
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
